import React, { useEffect } from "react";

import { graphql, useStaticQuery } from "gatsby";
import { useIsSSR } from "react-aria";
import Helmet from "react-helmet";
import { IntlProvider as RawIntlProvider } from "react-intl";

import de from "../intl/de.json";
import GlobalStyle from "./GlobalStyle";
import CookieConsent from "./shared/CookieConsent";
import { WithFooter } from "./shared/Footer";
import Robots from "./shared/Meta/Robots";
import { ModalPortalAnchor } from "./shared/Modal/Portal";
import { WithNavigationContainer } from "./shared/Navigation";
import useStore from "./shared/OrderUserAttribution/store";
import ScrollToTop from "./shared/ScrollToTop";
import SEO from "./shared/SEO";
import ShoppingCartFloatingButton from "./shared/ShoppingCartFloatingButton";
import Snackbar from "./shared/Snackbar";
import styled from "@emotion/styled";

const SkipLink = styled.a`
    position: absolute;
    top: -100px;
    left: 0;
    background: #000;
    color: #fff;
    padding: 8px 16px;
    transition: top 0.3s ease;
    text-decoration: none;
    z-index: 1000;
    &:focus {
        top: 0;
        outline: 2px solid #fff;
    }
`;

export const IntlProvider = ({ children, ...props }) => {
    const intl = { language: "de", defaultLocale: "de", messages: de };

    return (
        <RawIntlProvider
            locale={intl.language}
            defaultLocale={intl.defaultLocale}
            messages={intl.messages}
            formats={{
                date: {
                    short: {
                        month: "2-digit",
                        day: "2-digit",
                        year: "numeric",
                    },
                    full: {
                        month: "2-digit",
                        day: "2-digit",
                        year: "numeric",
                    },
                },
            }}
            {...props}
            timeZone="Europe/Berlin"
        >
            {children}
        </RawIntlProvider>
    );
};

const RootLayout = ({ location, children, data }) => {
    const { defaultFooter } = useStaticQuery(graphql`
        query {
            defaultFooter: footersYaml(yamlId: { eq: "default" }) {
                ...Footer
            }
        }
    `);

    const [setReferrer, setParameter] = useStore((state) => [
        state.setReferrer,
        state.setParameter,
    ]);

    const { slug, meta = {}, footer = defaultFooter } = data?.page ?? {};

    const isSSR = useIsSSR();

    const isLegacyPage = !data || (data.page?.isLegacyPage ?? false);

    useEffect(() => {
        if (!isSSR) {
            setReferrer();
            setParameter(location);
        }
    }, [isSSR]);
    // View for external usage of some components
    // used via webview/iframe do not Show Navigation or Footer in this Layout.
    if (children?.props?.pageContext?.isWebView === true) {
        return (
            <WebView slug={slug} meta={meta} isLegacyPage={isLegacyPage}>
                <Snackbar />
                {children}
            </WebView>
        );
    }

    return (
        <IntlProvider>
            <Snackbar />
            <ModalPortalAnchor />

            <GlobalStyle />

            <SEO {...{ slug, ...meta }} />

            <Robots />

            {isLegacyPage && (
                <Helmet htmlAttributes={{ class: "legacyPage" }} />
            )}
            <CookieConsent />
            <SkipLink href="#main" className="skip-link">
                Zum Hauptinhalt springen
            </SkipLink>
            <SkipLink href="#footer" className="skip-link">
                Zum Footer springen
            </SkipLink>

            <WithNavigationContainer
                organization={footer?.contact?.organization}
            >
                <WithFooter footer={footer}>
                    <main>{children}</main>
                </WithFooter>
            </WithNavigationContainer>
            <ScrollToTop />
        </IntlProvider>
    );
};
export default RootLayout;

const WebView = React.memo(({ slug, meta, isLegacyPage, children }) => {
    const isSSR = useIsSSR();

    return (
        <IntlProvider>
            <Snackbar />
            <GlobalStyle />
            <SEO {...{ slug, ...meta }} />

            <Robots index={false} />

            {isLegacyPage && (
                <Helmet htmlAttributes={{ class: "legacyPage" }} />
            )}
            <main>{children}</main>
            <ShoppingCartFloatingButton />
            <ScrollToTop />
            <ModalPortalAnchor />
        </IntlProvider>
    );
});

export const query = graphql`
    fragment Page on PageBase {
        footer {
            ...Footer
        }
        meta {
            title
            description
            lang
            altLangs {
                hreflang
                href
            }
        }
        slug
    }

    fragment PageItem on PageItemBase {
        footer {
            ...Footer
        }
        meta {
            title
            description
            lang
            altLangs {
                hreflang
                href
            }
        }
    }
`;
